import { useCallback, useEffect, useRef, useState } from 'react';

import { errorMessage } from '../services/apiError';
import {
  BUSY_STATUSES,
  gmailInviteService,
  GmailInviteBatch,
  GmailInviteStatus,
  InviteTarget,
} from '../services/gmailInviteService';
import { forceConsent } from '../utils/recordingBlur';

const POLL_MS = 2000;
const TARGET_KEY = 'gmail_invite_target';

const currentPath = (): string => {
  const params = new URLSearchParams(window.location.search);

  params.delete('gmail_invites');
  params.delete('gmail_invites_error');
  const query = params.toString();

  return `${window.location.pathname}${query ? `?${query}` : ''}`;
};

/** The target chosen before going to Google, so it is still chosen when Google sends the user back. */
export const rememberTarget = (target: InviteTarget): void => {
  try {
    sessionStorage.setItem(TARGET_KEY, JSON.stringify(target));
  } catch {
    // Blocked storage: the user picks the target again after Google.
  }
};

export const recallTarget = (): InviteTarget | null => {
  try {
    const raw = sessionStorage.getItem(TARGET_KEY);

    return raw ? JSON.parse(raw) as InviteTarget : null;
  } catch {
    return null;
  }
};

let statusRequest: Promise<GmailInviteStatus> | null = null;

/** Whether this user may invite from Gmail; one request per page load, shared by every button. */
export const useGmailInviteAvailable = (enabled: boolean): boolean => {
  const [available, setAvailable] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;

    statusRequest = statusRequest ?? gmailInviteService.status();
    statusRequest.then((status) => { if (!cancelled) setAvailable(status.available); })
      .catch(() => { statusRequest = null; });

    return () => { cancelled = true; };
  }, [enabled]);

  return available;
};

/** One batch, refreshed every two seconds while it is still reading contacts or sending. */
const useBatchFollower = () => {
  const [batch, setBatch] = useState<GmailInviteBatch | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const timer = useRef<number | null>(null);

  const follow = useCallback((batchId: string) => {
    const tick = async () => {
      try {
        const next = await gmailInviteService.batch(batchId);

        setBatch(next);
        if (BUSY_STATUSES.has(next.status)) timer.current = window.setTimeout(() => void tick(), POLL_MS);
      } catch (err) {
        setLoadError(errorMessage(err, 'We couldn’t load your invites. Close this window and try again.'));
      }
    };

    if (timer.current !== null) window.clearTimeout(timer.current);
    void tick();
  }, []);

  useEffect(() => () => {
    if (timer.current !== null) window.clearTimeout(timer.current);
  }, []);

  return { batch, follow, loadError };
};

const useInviteStatus = (active: boolean, follow: (batchId: string) => void) => {
  const [status, setStatus] = useState<GmailInviteStatus | null>(null);

  useEffect(() => {
    if (!active) return undefined;
    let cancelled = false;

    void gmailInviteService.status().then((next) => {
      if (cancelled) return;
      setStatus(next);
      if (next.available && next.latest_batch) follow(next.latest_batch.id);
    }).catch(() => undefined);

    return () => { cancelled = true; };
  }, [active, follow]);

  return status;
};

const useInviteActions = (batch: GmailInviteBatch | null, follow: (batchId: string) => void) => {
  const [actionError, setActionError] = useState<string | null>(null);
  const [connecting, setConnecting] = useState(false);

  const connect = useCallback(async (target: InviteTarget) => {
    setActionError(null);
    setConnecting(true);
    rememberTarget(target);
    try {
      window.location.assign(await gmailInviteService.connect(currentPath(), forceConsent()));
    } catch (err) {
      setConnecting(false);
      setActionError(errorMessage(err, 'Gmail could not be connected. Try again.'));
    }
  }, []);

  const send = useCallback(async (emails: string[], target: InviteTarget) => {
    if (!batch) return;
    setActionError(null);
    try {
      const result = await gmailInviteService.send(batch.id, emails, target, currentPath());

      if ('reconnect_url' in result) window.location.assign(result.reconnect_url);
      else follow(batch.id);
    } catch (err) {
      setActionError(errorMessage(err, 'The invites could not be sent. Try again.'));
    }
  }, [batch, follow]);

  const discard = useCallback(async () => {
    if (!batch) return;
    try {
      await gmailInviteService.discard(batch.id);
      follow(batch.id);
    } catch (err) {
      setActionError(errorMessage(err, 'The list could not be discarded. Try again.'));
    }
  }, [batch, follow]);

  return { actionError, connect, connecting, discard, send };
};

export interface UseGmailInviteResult {
  status: GmailInviteStatus | null;
  batch: GmailInviteBatch | null;
  error: string | null;
  connecting: boolean;
  connect: (target: InviteTarget) => Promise<void>;
  send: (emails: string[], target: InviteTarget) => Promise<void>;
  discard: () => Promise<void>;
}

/** The invite window's state: availability, the latest batch and its progress, and what the user can do. */
export const useGmailInvite = (active: boolean, notice?: string | null): UseGmailInviteResult => {
  const { batch, follow, loadError } = useBatchFollower();
  const status = useInviteStatus(active, follow);
  const { actionError, ...actions } = useInviteActions(batch, follow);

  return { status, batch, error: actionError ?? loadError ?? notice ?? null, ...actions };
};
